<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore, routeForBackendAuthState, type BackendAuthState } from '@/stores/auth';
import { requestEmailVerification, confirmEmailVerification } from '@/api';
import { extractErrorCode, gateErrorMessage } from '@/utils/errorHandler';
import { recaptchaHeaders } from '@/utils/recaptcha';
import { logger } from '@/utils/logger';
import GaiaAuthCard from '@/components/design/domain/auth/GaiaAuthCard.vue';
import GaiaAuthStatus from '@/components/design/domain/auth/GaiaAuthStatus.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaLink from '@/components/design/base/GaiaLink.vue';

// Kept in sync with InviteAcceptView: when a verification detour happens
// mid-invite, the stored token routes the user back to the accept page
// instead of the self-serve signup flow.
const INVITE_TOKEN_STORAGE_KEY = 'gaia-invite-token';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

/**
 * - 'session': default screen — resend / "I've verified" (requires a session)
 * - 'confirming': a mail-link token is being confirmed
 * - 'confirm-failed': confirm hit the gate (recaptcha/rate limit) — retryable
 * - 'success-anonymous': confirmed without a session — nudge to log in
 * - 'link-invalid': 410 — expired or already-used link
 */
type Phase = 'session' | 'confirming' | 'confirm-failed' | 'success-anonymous' | 'link-invalid';

const phase = ref<Phase>('session');
const error = ref<string | null>(null);
const info = ref<string | null>(null);
const confirming = ref(false);
const resending = ref(false);
const resendCooldown = ref(0);

// The mail link carries a single-use backend token (valid 1 h).
const mailToken = computed((): string | null =>
  typeof route.query.token === 'string' ? route.query.token : null
);

// Whether a verification mail is known to be on its way. Signup arrives with
// ?sent=1 right after requesting one; every other entry (legacy accounts,
// guard redirects) starts without and gets the re-verify variant with a
// "Send verification link" button instead of a resend.
const mailSent = ref('sent' in route.query);

/**
 * Post-verification routing. An invitee's verification detour must return to
 * the accept page (the `verified` flag re-triggers its confirm hook) — never
 * to the self-serve signup flow, which would create a duplicate organisation.
 */
function routeAfterValidation(state: BackendAuthState): void {
  const inviteToken = localStorage.getItem(INVITE_TOKEN_STORAGE_KEY);
  if (inviteToken) {
    void router.replace({ path: '/invite/accept', query: { token: inviteToken, verified: '1' } });
    return;
  }
  if (state === 'ok' || state === 'unprovisioned') {
    void router.replace(routeForBackendAuthState(state));
  } else {
    // 'inactive' / 'error' / 'unknown' — the session is gone; the auth
    // store holds the explanatory message for the login screen.
    void router.replace('/login');
  }
}

async function runConfirm(): Promise<void> {
  if (!mailToken.value) return;

  phase.value = 'confirming';
  error.value = null;
  info.value = null;
  try {
    const { error: apiError, response } = await confirmEmailVerification({
      body: { token: mailToken.value },
      headers: await recaptchaHeaders('email_verification_confirm'),
      throwOnError: false,
    });

    if (response?.ok) {
      if (authStore.user) {
        const state = await authStore.refreshBackendValidation();
        if (state === 'unverified') {
          // Confirm succeeded but the fresh token still lags — very unlikely;
          // fall back to the manual "I've verified" screen.
          phase.value = 'session';
          error.value = 'Almost there — please click "I\'ve verified my email" in a moment.';
          return;
        }
        routeAfterValidation(state);
      } else {
        phase.value = 'success-anonymous';
      }
      return;
    }

    if (extractErrorCode(apiError) === 'email_verification_token_invalid') {
      phase.value = 'link-invalid';
      return;
    }

    logger.error('Email verification confirm failed:', apiError);
    phase.value = 'confirm-failed';
    error.value =
      gateErrorMessage(response, apiError) ??
      'Could not verify your email. Please try again in a moment.';
  } catch (error_: unknown) {
    logger.error('Email verification confirm failed:', error_);
    phase.value = 'confirm-failed';
    error.value = 'Could not verify your email. Please try again in a moment.';
  }
}

async function handleResend(): Promise<void> {
  if (!authStore.user) {
    void router.replace('/login');
    return;
  }

  resending.value = true;
  error.value = null;
  info.value = null;
  try {
    const { error: apiError, response } = await requestEmailVerification({
      headers: await recaptchaHeaders('email_verification_request'),
      throwOnError: false,
    });
    if (!response?.ok) {
      error.value =
        gateErrorMessage(response, apiError) ??
        'Could not send the verification email. Please try again in a moment.';
      return;
    }
    phase.value = 'session';
    mailSent.value = true;
    info.value = 'Verification email sent. Please check your inbox.';
    resendCooldown.value = 30;
    const timer = window.setInterval(() => {
      resendCooldown.value -= 1;
      if (resendCooldown.value <= 0) {
        window.clearInterval(timer);
      }
    }, 1000);
  } catch (error_: unknown) {
    error.value = 'Could not send the verification email. Please try again in a moment.';
    logger.error('Error requesting verification email:', error_);
  } finally {
    resending.value = false;
  }
}

// "I've verified": the flag was flipped elsewhere (mail link in another tab);
// force-refresh the ID token so it carries email_verified and revalidate.
async function handleConfirm(): Promise<void> {
  confirming.value = true;
  error.value = null;
  info.value = null;
  try {
    const state = await authStore.refreshBackendValidation();
    if (state === 'unverified') {
      error.value =
        'Your email is still not verified. Please click the link in the email we sent you.';
    } else {
      routeAfterValidation(state);
    }
  } finally {
    confirming.value = false;
  }
}

async function handleSignOut(): Promise<void> {
  await authStore.logout();
  void router.replace('/login');
}

onMounted((): void => {
  // Arriving via the verification mail — confirm the token unprompted. Works
  // with or without a Firebase session (the token itself is the credential).
  if (mailToken.value) {
    void runConfirm();
  }
});
</script>

<template>
  <!-- AuthLayout draws the split screen; this page is only the card. Every
       branch below is one GaiaAuthStatus with a different tone and glyph. -->
  <gaia-auth-card>
    <!-- Mail-link token being confirmed -->
    <gaia-auth-status v-if="phase === 'confirming'" loading>
      Confirming your email…
    </gaia-auth-status>

    <!-- Confirmed without a session -->
    <gaia-auth-status
      v-else-if="phase === 'success-anonymous'"
      tone="success"
      title="Email verified"
    >
      <template #icon>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M4 10.5 8.2 14.5 16 6" />
        </svg>
      </template>
      Your email address is verified — log in to continue.
      <template #actions>
        <gaia-button type="primary" size="large" block @click="router.push('/login?verified=1')">
          Go to login
        </gaia-button>
      </template>
    </gaia-auth-status>

    <!-- Expired or already-used link -->
    <gaia-auth-status
      v-else-if="phase === 'link-invalid'"
      tone="warning"
      title="Verification link expired"
    >
      <template #icon>
        <svg
          width="19"
          height="19"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
        >
          <circle cx="10" cy="10" r="7" />
          <path d="M10 6v4.4l2.8 1.8" />
        </svg>
      </template>
      This link has expired or was already used.
      <template #actions>
        <gaia-banner v-if="error" plain type="error">{{ error }}</gaia-banner>
        <gaia-button
          v-if="authStore.user"
          type="primary"
          size="large"
          block
          :loading="resending"
          @click="handleResend"
        >
          Send a new verification email
        </gaia-button>
        <gaia-button v-else type="primary" size="large" block @click="router.push('/login')">
          Go to login
        </gaia-button>
        <n-flex justify="center">
          <gaia-link to="/login">Go to login</gaia-link>
        </n-flex>
      </template>
    </gaia-auth-status>

    <!-- Confirm hit the gate — retryable -->
    <template v-else-if="phase === 'confirm-failed'">
      <gaia-auth-status tone="alert" title="Verify your email">
        <template #icon>
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M10 3 1.8 17h16.4L10 3Z" />
            <path d="M10 8.6v3.6" />
            <circle cx="10" cy="14.8" r=".4" fill="currentColor" />
          </svg>
        </template>
        <template #actions>
          <gaia-banner v-if="error" plain type="error">{{ error }}</gaia-banner>
          <gaia-button type="primary" size="large" block @click="runConfirm">Try again</gaia-button>
          <n-flex justify="center">
            <gaia-link to="/login">Go to login</gaia-link>
          </n-flex>
        </template>
      </gaia-auth-status>
    </template>

    <!-- Default: session screen. `mailSent` decides which action is primary —
         a mail is already out, so confirming beats sending another. -->
    <gaia-auth-status v-else title="Verify your email">
      <template #icon>
        <svg
          v-if="mailSent"
          width="19"
          height="19"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect x="2" y="4.5" width="16" height="11.5" rx="2" />
          <path d="m2.6 5.4 7.4 6 7.4-6" />
        </svg>
        <svg
          v-else
          width="19"
          height="19"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect x="4" y="8" width="12" height="9" rx="2" />
          <path d="M7 8V6a3 3 0 0 1 6 0v2" />
        </svg>
      </template>

      <template v-if="mailSent">
        We sent a verification link to <strong>{{ authStore.sessionEmail }}</strong
        >. Open it, then come back here.
      </template>
      <template v-else>
        For security reasons you need to verify
        <strong>{{ authStore.sessionEmail }}</strong> before continuing.
      </template>

      <template #actions>
        <gaia-banner v-if="error" plain type="error">{{ error }}</gaia-banner>
        <gaia-banner v-if="info" plain type="success">{{ info }}</gaia-banner>

        <template v-if="mailSent">
          <gaia-button
            type="primary"
            size="large"
            block
            :loading="confirming"
            @click="handleConfirm"
          >
            I've verified my email
          </gaia-button>
          <n-flex justify="center">
            <gaia-link :disabled="resendCooldown > 0" @click="handleResend">
              {{ resendCooldown > 0 ? `Resend email (${resendCooldown}s)` : 'Resend email' }}
            </gaia-link>
          </n-flex>
        </template>

        <template v-else>
          <gaia-button type="primary" size="large" block :loading="resending" @click="handleResend">
            Send verification link
          </gaia-button>
          <n-flex justify="center">
            <gaia-link @click="handleConfirm">I've already verified</gaia-link>
          </n-flex>
        </template>
      </template>
    </gaia-auth-status>
  </gaia-auth-card>

  <n-flex justify="center" :style="{ marginTop: '18px' }">
    <gaia-link tone="muted" @click="handleSignOut">Sign out</gaia-link>
  </n-flex>
</template>
